import { useState, useEffect, useRef, useCallback } from "react";
import { API, getAuthHeaders, authFetch } from "@/App";
import { toast } from "sonner";
import { HIGHLIGHT_DURATION_MS } from "@/components/tableau";

const ORDER_SYNC_CHANNEL = "orderflow_order_sync";

export function useTableData(user, navigate) {
  const [orders, setOrders] = useState([]);
  const [columns, setColumns] = useState([]);
  const [columnOrder, setColumnOrder] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [availableStatuses, setAvailableStatuses] = useState([]);
  const [availableTypes, setAvailableTypes] = useState([]);

  // Counts
  const [attachmentCounts, setAttachmentCounts] = useState({});
  const [emailCounts, setEmailCounts] = useState({});
  const [commentCounts, setCommentCounts] = useState({});
  const [fabricPhotos, setFabricPhotos] = useState({});

  // Modified rows tracking
  const [modifiedRows, setModifiedRows] = useState({});

  // Data version tracking
  const [dataVersion, setDataVersion] = useState(null);
  const [hasNewChanges, setHasNewChanges] = useState(false);
  const [changedBy, setChangedBy] = useState([]);
  const [lastChangeCheck, setLastChangeCheck] = useState(null);
  // Server time of the reference version: writes by other users after it trigger the banner
  const changesSinceRef = useRef(null);

  // Config
  const [columnWidths, setColumnWidths] = useState({});
  const [serverColumnTypes, setServerColumnTypes] = useState({});
  const [configLoaded, setConfigLoaded] = useState(false);
  const [zoom, setZoom] = useState(100);

  // Refs for debounced saves
  const saveWidthsTimeoutRef = useRef(null);
  const saveOrderTimeoutRef = useRef(null);

  // ---- Load server config on mount ----
  useEffect(() => {
    const loadServerConfig = async () => {
      try {
        const response = await authFetch(`${API}/table-config`, { headers: getAuthHeaders() });
        if (response.ok) {
          const config = await response.json();
          if (config.column_widths && Object.keys(config.column_widths).length > 0) {
            setColumnWidths(config.column_widths);
          }
          if (config.column_order && config.column_order.length > 0) {
            setColumnOrder(config.column_order);
          }
          if (config.column_types && Object.keys(config.column_types).length > 0) {
            setServerColumnTypes(config.column_types);
          }
        }
      } catch (e) {
        console.error("Failed to load server config:", e);
      }
      setConfigLoaded(true);
    };

    const loadModifiedRows = async () => {
      try {
        const response = await authFetch(`${API}/modified-rows`, { headers: getAuthHeaders() });
        if (response.ok) {
          const data = await response.json();
          if (data.rows) setModifiedRows(data.rows);
        }
      } catch (e) {
        console.error("Failed to load modified rows:", e);
      }
    };

    loadServerConfig();
    loadModifiedRows();
  }, []);

  // ---- Polling for data changes ----
  useEffect(() => {
    const checkForChanges = async () => {
      try {
        const since = changesSinceRef.current;
        const response = await authFetch(`${API}/data-version${since ? `?since=${encodeURIComponent(since)}` : ""}`, { headers: getAuthHeaders() });
        if (response.ok) {
          const newVersion = await response.json();
          setLastChangeCheck(new Date());
          if (!dataVersion) {
            setDataVersion(newVersion);
            changesSinceRef.current = newVersion.server_time;
          } else if (newVersion.version !== dataVersion.version) {
            if (newVersion.changed_by?.length) {
              setChangedBy(newVersion.changed_by);
              setHasNewChanges(true);
            } else {
              // Only our own writes since the reference: adopt silently, keeping 60 s for writes still in progress
              setDataVersion(newVersion);
              changesSinceRef.current = new Date(Math.max(Date.parse(since), Date.parse(newVersion.server_time) - 60000)).toISOString();
            }
          } else if (since && newVersion.server_time) {
            // Unchanged: move the window forward, keeping 60 s for writes still in progress
            const floor = Date.parse(newVersion.server_time) - 60000;
            if (floor > Date.parse(since)) changesSinceRef.current = new Date(floor).toISOString();
          }
        }
      } catch (e) {
        console.error("Failed to check data version:", e);
      }
    };

    const initialTimeout = setTimeout(checkForChanges, 5000);
    const interval = setInterval(checkForChanges, 15000);
    return () => { clearTimeout(initialTimeout); clearInterval(interval); };
  }, [dataVersion]);

  // ---- Refresh data ----
  const refreshData = async () => {
    setHasNewChanges(false);
    setIsLoading(true);
    try {
      const [ordersRes, columnsRes, versionRes] = await Promise.all([
        authFetch(`${API}/orders`, { headers: getAuthHeaders() }),
        authFetch(`${API}/columns`, { headers: getAuthHeaders() }),
        authFetch(`${API}/data-version`, { headers: getAuthHeaders() })
      ]);
      if (ordersRes.ok) { const data = await ordersRes.json(); setOrders(data.orders || []); }
      if (columnsRes.ok) { const data = await columnsRes.json(); setColumns(data.columns || []); }
      if (versionRes.ok) { const newVersion = await versionRes.json(); setDataVersion(newVersion); changesSinceRef.current = newVersion.server_time; setChangedBy([]); }
      toast.success("Données actualisées");
    } catch (e) {
      toast.error("Erreur lors de l'actualisation");
    } finally {
      setIsLoading(false);
    }
  };

  // ---- Save column widths (debounced) ----
  useEffect(() => {
    if (!configLoaded || Object.keys(columnWidths).length === 0) return;
    if (saveWidthsTimeoutRef.current) clearTimeout(saveWidthsTimeoutRef.current);
    saveWidthsTimeoutRef.current = setTimeout(async () => {
      try {
        await authFetch(`${API}/table-config/widths`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(columnWidths),
        });
      } catch (e) { console.error("Failed to save column widths:", e); }
    }, 500);
    return () => { if (saveWidthsTimeoutRef.current) clearTimeout(saveWidthsTimeoutRef.current); };
  }, [columnWidths, configLoaded]);

  // ---- Save column order (debounced) ----
  useEffect(() => {
    if (!configLoaded || columnOrder.length === 0) return;
    if (saveOrderTimeoutRef.current) clearTimeout(saveOrderTimeoutRef.current);
    saveOrderTimeoutRef.current = setTimeout(async () => {
      try {
        await authFetch(`${API}/table-config/order`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(columnOrder),
        });
      } catch (e) { console.error("Failed to save column order:", e); }
    }, 500);
    return () => { if (saveOrderTimeoutRef.current) clearTimeout(saveOrderTimeoutRef.current); };
  }, [columnOrder, configLoaded]);

  // ---- Cleanup expired modified rows ----
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setModifiedRows(prev => {
        const updated = {};
        let hasChanges = false;
        Object.entries(prev).forEach(([orderId, timestamp]) => {
          if (now - timestamp < HIGHLIGHT_DURATION_MS) { updated[orderId] = timestamp; }
          else { hasChanges = true; }
        });
        return hasChanges ? updated : prev;
      });
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // ---- Modified rows helpers ----
  const markRowAsModified = useCallback(async (orderId) => {
    const now = Date.now();
    setModifiedRows(prev => ({ ...prev, [orderId]: now }));
    try { await authFetch(`${API}/modified-rows/${orderId}`, { method: "POST" }); } catch (e) {}
  }, []);

  const isRowHighlighted = useCallback((orderId) => {
    const timestamp = modifiedRows[orderId];
    if (!timestamp) return false;
    return Date.now() - timestamp < HIGHLIGHT_DURATION_MS;
  }, [modifiedRows]);

  const markRowAsSeen = useCallback(async (orderId) => {
    setModifiedRows(prev => { const updated = { ...prev }; delete updated[orderId]; return updated; });
    try { await authFetch(`${API}/modified-rows/${orderId}`, { method: "DELETE" }); } catch (e) {}
  }, []);

  const markAllAsSeen = useCallback(async () => {
    setModifiedRows({});
    try { await authFetch(`${API}/modified-rows`, { method: "DELETE" }); } catch (e) {}
  }, []);

  const highlightedRowsCount = Object.entries(modifiedRows).filter(
    ([, timestamp]) => Date.now() - timestamp < HIGHLIGHT_DURATION_MS
  ).length;

  // ---- Initialize column order when columns load ----
  useEffect(() => {
    if (columns.length > 0 && columnOrder.length === 0) {
      setColumnOrder(columns.map(c => c.name));
    } else if (columns.length > 0) {
      const colNames = columns.map(c => c.name);
      const newOrder = columnOrder.filter(name => colNames.includes(name));
      const addedCols = colNames.filter(name => !columnOrder.includes(name));
      setColumnOrder([...newOrder, ...addedCols]);
    }
  }, [columns]);

  // ---- Redirect if not logged in ----
  useEffect(() => {
    if (!user) { navigate("/"); return; }
    fetchData();
  }, [user, navigate]);

  // ---- BroadcastChannel sync ----
  useEffect(() => {
    const channel = new BroadcastChannel(ORDER_SYNC_CHANNEL);
    channel.onmessage = (event) => {
      if (event.data.type === 'NEW_ORDER') {
        const newOrder = event.data.order;
        setOrders(prevOrders => {
          if (prevOrders.some(o => o.order_id === newOrder.order_id)) return prevOrders;
          return [newOrder, ...prevOrders];
        });
        markRowAsModified(newOrder.order_id);
        toast.success("Nouvelle commande ajoutée !", {
          description: newOrder.extracted_data?.NAME || newOrder.extracted_data?.client_name || "Commande",
          duration: 4000,
        });
      }
    };
    return () => channel.close();
  }, [markRowAsModified]);

  // ---- Main fetch ----
  const fetchData = async () => {
    setIsLoading(true);
    try {
      // Reference version taken before loading, so any later write by another user shows the banner
      const versionRes = await authFetch(`${API}/data-version`, { headers: getAuthHeaders() });
      if (versionRes.ok) {
        const v = await versionRes.json();
        setDataVersion(v);
        changesSinceRef.current = v.server_time;
        setHasNewChanges(false);
        setChangedBy([]);
      }
      const columnsRes = await authFetch(`${API}/columns`, { headers: getAuthHeaders() });
      if (columnsRes.ok) { const data = await columnsRes.json(); setColumns(data.columns || []); }

      const ordersRes = await authFetch(`${API}/orders`, { headers: getAuthHeaders() });
      if (ordersRes.ok) {
        const data = await ordersRes.json();
        const loadedOrders = data.orders || [];
        setOrders(loadedOrders);
        const statuses = new Set();
        const types = new Set();
        loadedOrders.forEach(order => {
          const extracted = order.extracted_data || {};
          const statusValue = extracted["order_status"] || extracted["ORDER STATUS"] || extracted["order.status"] || extracted["ORDER.STATUS"] || extracted["status"];
          if (statusValue && String(statusValue).trim()) statuses.add(String(statusValue).trim());
          const typeValue = extracted["TYPE"] || extracted["type"] || extracted["Type"];
          if (typeValue && String(typeValue).trim()) types.add(String(typeValue).trim());
        });
        setAvailableStatuses(Array.from(statuses).sort());
        setAvailableTypes(Array.from(types).sort());
      }

      const countsRes = await authFetch(`${API}/orders/all/counts`, { headers: getAuthHeaders() });
      if (countsRes.ok) {
        const counts = await countsRes.json();
        setAttachmentCounts(counts.attachments || {});
        setEmailCounts(counts.emails || {});
        setFabricPhotos(counts.fabric_photos || {});
      }
    } catch (error) {
      toast.error("Erreur lors du chargement des données");
    } finally {
      setIsLoading(false);
    }
  };

  // Get columns in display order
  const getOrderedColumns = useCallback(() => {
    if (columnOrder.length === 0) return columns;
    return columnOrder.map(name => columns.find(c => c.name === name)).filter(Boolean);
  }, [columnOrder, columns]);

  return {
    orders, setOrders,
    columns, setColumns,
    columnOrder, setColumnOrder,
    isLoading, setIsLoading,
    availableStatuses, setAvailableStatuses,
    availableTypes, setAvailableTypes,
    attachmentCounts, setAttachmentCounts,
    emailCounts, setEmailCounts,
    commentCounts, setCommentCounts,
    fabricPhotos, setFabricPhotos,
    modifiedRows,
    dataVersion, hasNewChanges, setHasNewChanges, changedBy, lastChangeCheck,
    columnWidths, setColumnWidths,
    serverColumnTypes,
    configLoaded,
    zoom, setZoom,
    fetchData, refreshData,
    markRowAsModified, isRowHighlighted, markRowAsSeen, markAllAsSeen,
    highlightedRowsCount,
    getOrderedColumns,
  };
}
